import { useMemo, useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { Card, Title } from "@tremor/react";
import {
  Button,
  Drawer,
  Empty,
  Input,
  List,
  Popconfirm,
  Segmented,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import dayjs from "dayjs";

import {
  COLLECTIONS_BANK_TRANSACTION_STATUS_LABELS,
  CollectionsBankTransactionStatus,
  type CollectionsBankCandidate,
  type CollectionsBankTransaction,
} from "@fitness-app/data-models/entities/CollectionsBankTransaction";

import { type ConfirmPaymentDocumentIssuance } from "~/modules/Products/hooks/useConfirmManualPayment";
import ConfirmPaymentModal from "~/modules/Products/Product/ProductOrders/ConfirmPaymentModal";

import { formatPln } from "../collectionsLabels";
import {
  useBankTransactions,
  useCollectionsCase,
  useCollectionsCases,
  useIgnoreBankTransaction,
  useImportBankCsv,
  useSettleBankTransaction,
} from "../hooks/useCollections";
import { urlPagination, useCollectionsUrlState } from "../hooks/useCollectionsUrlState";

/** What a transfer is about to be booked against — from the matcher or picked by hand. */
interface Target {
  orderId: string;
  productId: string;
  clientEmail: string;
  label: string;
}

const STATUS_OPTIONS = [
  { value: CollectionsBankTransactionStatus.Unmatched, label: "Do przypisania" },
  { value: CollectionsBankTransactionStatus.Proposed, label: "Dopasowane" },
  { value: CollectionsBankTransactionStatus.Settled, label: "Zaksięgowane" },
  { value: CollectionsBankTransactionStatus.Ignored, label: "Pominięte" },
];

const candidateLabel = (candidate: CollectionsBankCandidate) =>
  `${candidate.clientName ?? candidate.clientEmail} · rata ${candidate.paymentCycle ?? "?"} · ${formatPln(candidate.amount)}${
    candidate.orderNumber ? ` · zam. ${candidate.orderNumber}` : ""
  }`;

const targetOf = (candidate: CollectionsBankCandidate): Target => ({
  orderId: candidate.orderId,
  productId: candidate.productId,
  clientEmail: candidate.clientEmail,
  label: candidateLabel(candidate),
});

/** Pekao exports come as windows-1250 or UTF-8; a strict UTF-8 decode tells them apart. */
const decodeStatement = (buffer: ArrayBuffer): string => {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("windows-1250").decode(buffer);
  }
};

/** Search for a case and pick one of its unpaid raty — for transfers the matcher could not place. */
const PickInstallmentDrawer = ({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (target: Target) => void;
}) => {
  const [query, setQuery] = useState("");
  const [caseId, setCaseId] = useState<string | undefined>(undefined);
  const cases = useCollectionsCases({ query: query || undefined });
  const details = useCollectionsCase(caseId);

  return (
    <Drawer title="Przypisz do raty" open={open} onClose={onClose} width={560}>
      <Input.Search
        placeholder="E-mail lub nazwisko klientki"
        allowClear
        onSearch={(value) => {
          setQuery(value.trim());
          setCaseId(undefined);
        }}
      />
      {!caseId ? (
        <List
          className="mt-3"
          size="small"
          loading={cases.isLoading}
          dataSource={cases.data ?? []}
          locale={{ emptyText: "Brak spraw" }}
          renderItem={(item) => (
            <List.Item onClick={() => setCaseId(item.id)} className="cursor-pointer">
              <div>
                <div>{item.clientName ?? item.clientEmail}</div>
                <Typography.Text type="secondary" className="text-xs">
                  {item.clientEmail} · zaległość {formatPln(item.balance.overdueAmount)}
                </Typography.Text>
              </div>
            </List.Item>
          )}
        />
      ) : (
        <div className="mt-3">
          <Button type="link" className="px-0" onClick={() => setCaseId(undefined)}>
            ← inna sprawa
          </Button>
          <List
            size="small"
            loading={details.isLoading}
            dataSource={(details.data?.contracts ?? []).flatMap((contract) =>
              contract.invoices.map((invoice) => ({ contract, invoice })),
            )}
            locale={{ emptyText: "Ta sprawa nie ma nieopłaconych rat" }}
            renderItem={({ contract, invoice }) => (
              <List.Item
                className="cursor-pointer"
                onClick={() =>
                  onPick({
                    orderId: invoice.orderId,
                    productId: invoice.productId,
                    clientEmail: details.data!.case.clientEmail,
                    label: `${details.data!.clientName ?? details.data!.case.clientEmail} · rata ${invoice.paymentCycle ?? "?"} · ${formatPln(invoice.amount)}`,
                  })
                }
              >
                <div>
                  <div>
                    {contract.productName ?? contract.productId} · rata {invoice.paymentCycle ?? "?"}
                  </div>
                  <Typography.Text type="secondary" className="text-xs">
                    {formatPln(invoice.amount)} · termin {dayjs(invoice.dueAt).format("DD.MM.YYYY")}
                    {invoice.orderNumber ? ` · zam. ${invoice.orderNumber}` : ""}
                  </Typography.Text>
                </div>
              </List.Item>
            )}
          />
        </div>
      )}
    </Drawer>
  );
};

/**
 * Transfers from the Pekao statement (TRE-2172): upload the CSV, book the
 * ones the matcher recognised, assign the rest by hand, skip what is not a rata.
 */
const BankTransfersPanel = () => {
  const [urlState, setUrlState] = useCollectionsUrlState();
  const status = urlState.bankStatus;
  const transactions = useBankTransactions([status]);
  const importCsv = useImportBankCsv();
  const settle = useSettleBankTransaction();
  const ignore = useIgnoreBankTransaction();
  const [targets, setTargets] = useState<Record<string, Target>>({});
  const [picking, setPicking] = useState<string | null>(null);
  const [settling, setSettling] = useState<CollectionsBankTransaction | null>(null);

  const rows = transactions.data ?? [];
  const targetFor = (row: CollectionsBankTransaction): Target | null => {
    if (targets[row.id]) return targets[row.id]!;
    const proposed = row.candidates.find((candidate) => candidate.orderId === row.proposedOrderId);
    return proposed ? targetOf(proposed) : null;
  };

  const confirmSettle = (documentIssuance?: ConfirmPaymentDocumentIssuance) => {
    if (!settling) return;
    const target = targetFor(settling);
    if (!target) return;
    settle.mutate(
      { transactionId: settling.id, ...target, documentIssuance },
      {
        onSettled: () => setSettling(null),
      },
    );
  };

  const columns = useMemo(
    () => [
      {
        title: "Data",
        dataIndex: "bookedAt",
        key: "bookedAt",
        width: 100,
        render: (value: string) => dayjs(value).format("DD.MM.YYYY"),
      },
      {
        title: "Kwota",
        dataIndex: "amount",
        key: "amount",
        width: 110,
        render: (value: number) => <span className="font-medium">{formatPln(value)}</span>,
      },
      {
        title: "Nadawca",
        dataIndex: "counterparty",
        key: "counterparty",
        render: (value: string | null) => value ?? "—",
      },
      {
        title: "Tytuł",
        dataIndex: "title",
        key: "title",
        render: (value: string) => (
          <Tooltip title={value}>
            <span className="line-clamp-2">{value || "—"}</span>
          </Tooltip>
        ),
      },
      {
        title: "Rata",
        key: "target",
        width: 340,
        render: (_: unknown, row: CollectionsBankTransaction) => {
          if (row.status === CollectionsBankTransactionStatus.Settled) {
            return <Tag color="green">zaksięgowany · zam. {row.settledOrderId}</Tag>;
          }
          if (row.status === CollectionsBankTransactionStatus.Ignored) {
            return <Typography.Text type="secondary">{row.ignoredReason || "pominięty"}</Typography.Text>;
          }
          const target = targetFor(row);
          const options = row.candidates.map((candidate) => ({
            value: candidate.orderId,
            label: candidateLabel(candidate),
            candidate,
          }));
          const custom = target && !row.candidates.some((candidate) => candidate.orderId === target.orderId);
          return (
            <div className="flex flex-col gap-1">
              <Select
                size="small"
                className="w-full"
                placeholder={row.candidates.length ? "Wybierz ratę" : "Brak podpowiedzi — przypisz ręcznie"}
                value={target?.orderId}
                options={custom ? [{ value: target.orderId, label: target.label }, ...options] : options}
                onChange={(orderId) => {
                  const candidate = row.candidates.find((entry) => entry.orderId === orderId);
                  if (candidate) setTargets((prev) => ({ ...prev, [row.id]: targetOf(candidate) }));
                }}
                dropdownStyle={{ minWidth: 420 }}
              />
              {target && !custom ? (
                <Typography.Text type="secondary" className="text-xs">
                  {row.candidates.find((candidate) => candidate.orderId === target.orderId)?.reasons.join(", ")}
                </Typography.Text>
              ) : null}
              <Button type="link" size="small" className="w-fit px-0" onClick={() => setPicking(row.id)}>
                {row.candidates.length ? "Inna sprawa…" : "Znajdź sprawę…"}
              </Button>
            </div>
          );
        },
      },
      {
        title: "",
        key: "actions",
        width: 200,
        render: (_: unknown, row: CollectionsBankTransaction) => {
          // A booked transfer already paid its rata; only a skipped one can come back.
          if (row.status === CollectionsBankTransactionStatus.Settled) {
            return null;
          }
          if (row.status === CollectionsBankTransactionStatus.Ignored) {
            return (
              <Button size="small" onClick={() => ignore.mutate({ transactionId: row.id, reopen: true })}>
                Cofnij
              </Button>
            );
          }
          return (
            <div className="flex gap-2">
              <Button type="primary" size="small" disabled={!targetFor(row)} onClick={() => setSettling(row)}>
                Zaksięguj
              </Button>
              <Popconfirm
                title="Pominąć ten przelew?"
                description="To nie rata (zawieszenie, zwrot, przelew własny). Można cofnąć."
                okText="Pomiń"
                cancelText="Anuluj"
                onConfirm={() => ignore.mutate({ transactionId: row.id, reason: "nie rata" })}
              >
                <Button size="small">Pomiń</Button>
              </Popconfirm>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targets, ignore],
  );

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Title>Przelewy z banku</Title>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            value={status}
            onChange={(value) =>
              void setUrlState({ bankStatus: value as CollectionsBankTransactionStatus, bankPage: 1 })
            }
            options={STATUS_OPTIONS}
          />
          <Upload
            accept=".csv,.txt"
            showUploadList={false}
            beforeUpload={(file) => {
              void file.arrayBuffer().then((buffer) => {
                importCsv.mutate({ fileName: file.name, content: decodeStatement(buffer) });
              });
              return false;
            }}
          >
            <Button icon={<UploadOutlined />} loading={importCsv.isLoading}>
              Wczytaj wyciąg (CSV)
            </Button>
          </Upload>
        </div>
      </div>
      <Table
        className="mt-4"
        size="small"
        rowKey="id"
        loading={transactions.isLoading}
        columns={columns}
        dataSource={rows}
        pagination={urlPagination(urlState.bankPage, urlState.bankPageSize, (next) =>
          setUrlState({ bankPage: next.page, bankPageSize: next.pageSize }),
        )}
        scroll={{ x: true }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`Brak przelewów: ${COLLECTIONS_BANK_TRANSACTION_STATUS_LABELS[status].toLowerCase()}`}
            />
          ),
        }}
      />

      <PickInstallmentDrawer
        open={picking !== null}
        onClose={() => setPicking(null)}
        onPick={(target) => {
          if (picking) setTargets((prev) => ({ ...prev, [picking]: target }));
          setPicking(null);
        }}
      />

      <ConfirmPaymentModal
        open={settling !== null}
        loading={settle.isLoading}
        onCancel={() => setSettling(null)}
        onConfirm={confirmSettle}
      />
    </Card>
  );
};

export default BankTransfersPanel;
